import React, { useState, useEffect, useContext } from 'react';
import { AuthContext } from '../context/AuthContext';
import api from '../services/api';
import { FileText, Plus, Edit2, Trash2, X, Check, Sparkles, Users } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useNavigate } from 'react-router-dom';

const RequirementsPanel = () => {
  const { user } = useContext(AuthContext);
  const navigate = useNavigate();
  const isAdmin = user?.role === 'Admin';
  const [notices, setNotices] = useState([]);
  const [loading, setLoading] = useState(true);
  
  const [isEditing, setIsEditing] = useState(false);
  const [editId, setEditId] = useState(null);
  const [formData, setFormData] = useState({ title: '', description: '', assigned_to: '' });
  const [usersList, setUsersList] = useState([]);

  useEffect(() => {
    fetchNotices();
    if (isAdmin) {
      api.get('/users').then(res => setUsersList(res.data)).catch(console.error);
    }
  }, [isAdmin]);

  const fetchNotices = async () => {
    try {
      const res = await api.get('/requirements?is_document=true');
      setNotices(res.data);
    } catch (err) {
      console.error('Error fetching requirements:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleSave = async (e) => {
    e.preventDefault();
    const payload = {
      title: formData.title,
      description: formData.description,
      assigned_to: formData.assigned_to || null,
      is_document: true,
    };
    try {
      if (editId) {
        await api.put(`/requirements/${editId}`, payload);
      } else {
        await api.post('/requirements', payload);
      }
      setFormData({ title: '', description: '', assigned_to: '' });
      setIsEditing(false);
      setEditId(null);
      fetchNotices();
    } catch (err) {
      console.error('Error saving notice:', err);
    }
  };

  const handleEdit = (notice, e) => {
    e.stopPropagation();
    setFormData({ 
      title: notice.title, 
      description: notice.description || '', 
      assigned_to: notice.assigned_to || ''
    });
    setEditId(notice.id);
    setIsEditing(true);
  };

  const handleDelete = async (id, e) => {
    e.stopPropagation();
    if (!window.confirm('Are you sure you want to delete this notice?')) return;
    try {
      await api.delete(`/requirements/${id}`);
      fetchNotices();
    } catch (err) {
      console.error('Error deleting notice:', err);
    }
  };

  const handleNoticeClick = (id) => {
    navigate(`/notifications?id=${id}`);
  };

  if (loading) return (
    <div className="bg-white p-8 rounded-[3rem] border border-gray-100 shadow-2xl shadow-indigo-900/5 mt-8 animate-pulse">
       <div className="h-8 bg-gray-200 rounded-full w-1/3 mb-8"></div>
       <div className="space-y-4">
         <div className="h-24 bg-gray-50 rounded-[2rem] w-full"></div>
         <div className="h-24 bg-gray-50 rounded-[2rem] w-full"></div>
       </div>
    </div>
  );

  return (
    <div className="bg-white/70 backdrop-blur-xl p-8 md:p-10 rounded-[3rem] border border-white shadow-2xl shadow-indigo-900/10 mt-8 relative overflow-hidden">
      {/* Decorative background element */}
      <div className="absolute top-0 right-0 -mt-20 -mr-20 w-64 h-64 bg-rose-500/5 rounded-full blur-3xl pointer-events-none" />
      <div className="absolute bottom-0 left-0 -mb-20 -ml-20 w-64 h-64 bg-indigo-500/5 rounded-full blur-3xl pointer-events-none" />

      <div className="flex flex-col md:flex-row md:items-center justify-between mb-10 gap-6">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <span className="flex items-center gap-1 px-2 py-0.5 bg-indigo-50 text-indigo-500 rounded-full text-[8px] font-black uppercase tracking-wider border border-indigo-100/50">
              <FileText size={10} className="animate-pulse" /> Checkpoint
            </span>
          </div>
          <h2 className="text-3xl font-black text-gray-900 flex items-center gap-4 tracking-tight">
            Required Documents
            <Sparkles size={24} className="text-amber-400" />
          </h2>
          <p className="text-gray-400 text-sm font-medium mt-1">Ensure you have all necessary documentation ready for processing</p>
        </div>

        {isAdmin && !isEditing && (
          <motion.button 
            whileHover={{ scale: 1.05 }}
            whileTap={{ scale: 0.95 }}
            onClick={() => { setIsEditing(true); setEditId(null); setFormData({ title: '', description: '', assigned_to: '' }); }}
            className="flex items-center justify-center gap-2 px-8 py-4 bg-indigo-600 text-white rounded-2xl font-black text-sm hover:bg-indigo-700 transition-all shadow-xl shadow-indigo-200 group"
          >
            <Plus size={20} className="group-hover:rotate-90 transition-transform duration-300" /> 
            Add Document Topic
          </motion.button>
        )}
      </div>

      <AnimatePresence>
        {isEditing && isAdmin && (
          <motion.div 
            initial={{ opacity: 0, y: -20, scale: 0.95 }} 
            animate={{ opacity: 1, y: 0, scale: 1 }} 
            exit={{ opacity: 0, scale: 0.95 }}
            className="mb-10"
          >
            <form onSubmit={handleSave} className="bg-gradient-to-br from-indigo-50 to-white p-8 rounded-[2.5rem] border border-indigo-100 shadow-lg shadow-indigo-900/5 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-6">
                <button type="button" onClick={() => setIsEditing(false)} className="w-10 h-10 flex items-center justify-center bg-white rounded-full text-gray-400 hover:text-rose-600 shadow-sm border border-gray-100 transition-all">
                  <X size={20} />
                </button>
              </div>
              
              <h3 className="text-sm font-black text-indigo-900 uppercase tracking-widest mb-6 flex items-center gap-2">
                <div className="w-2 h-2 bg-indigo-600 rounded-full" />
                {editId ? 'Modify Document Entry' : 'New Document Requirement'}
              </h3>
              
              <div className="space-y-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                  <div>
                    <label className="block text-[10px] font-black text-indigo-900/50 uppercase tracking-widest mb-2 ml-1">Document Category / Title</label>
                    <input 
                      type="text" 
                      value={formData.title} 
                      onChange={e => setFormData({...formData, title: e.target.value})} 
                      className="w-full px-6 py-4 rounded-2xl bg-white border border-indigo-100 focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 font-bold text-gray-800 placeholder:text-gray-300 transition-all"
                      required 
                      placeholder="e.g. Important Update"
                    />
                  </div>
                  <div>
                    <label className="block text-[10px] font-black text-indigo-900/50 uppercase tracking-widest mb-2 ml-1">Target Audience</label>
                    <select 
                      value={formData.assigned_to} 
                      onChange={e => setFormData({...formData, assigned_to: e.target.value})} 
                      className="w-full px-6 py-4 rounded-2xl bg-white border border-indigo-100 focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 font-bold text-gray-800 placeholder:text-gray-300 transition-all appearance-none"
                    >
                      <option value="">Global Notice (Everyone)</option>
                      {usersList.map(u => (
                        <option key={u.id} value={u.id}>{u.full_name || u.username}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div>
                  <label className="block text-[10px] font-black text-indigo-900/50 uppercase tracking-widest mb-2 ml-1">Specific Details / Description</label>
                  <textarea 
                    value={formData.description} 
                    onChange={e => setFormData({...formData, description: e.target.value})} 
                    className="w-full px-6 py-4 rounded-2xl bg-white border border-indigo-100 focus:outline-none focus:ring-4 focus:ring-indigo-500/10 focus:border-indigo-500 font-medium text-gray-700 placeholder:text-gray-300 min-h-[120px] transition-all"
                    placeholder="List the details required..."
                  />
                </div>
                <div className="flex justify-end pt-2">
                  <button type="submit" className="flex items-center gap-2 px-10 py-4 bg-indigo-600 text-white rounded-2xl font-black text-sm shadow-xl shadow-indigo-200 hover:bg-indigo-700 hover:-translate-y-1 transition-all">
                    <Check size={20} /> Save Entry
                  </button>
                </div>
              </div>
            </form>
          </motion.div>
        )}
      </AnimatePresence>

      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {notices.length === 0 ? (
          <div className="col-span-full py-20 text-center border-4 border-dashed border-gray-50 rounded-[3rem] bg-gray-50/30">
            <div className="w-20 h-20 bg-white rounded-3xl flex items-center justify-center mx-auto mb-6 shadow-sm border border-gray-100">
              <FileText size={40} className="text-gray-200" />
            </div>
            <p className="text-lg font-black text-gray-400">No documents listed yet.</p>
          </div>
        ) : (
          notices.map((notice, index) => {
            const colors = [
              { bg: 'bg-indigo-50', text: 'text-indigo-600', hover: 'group-hover:bg-indigo-600', border: 'hover:border-indigo-200', shadow: 'hover:shadow-indigo-900/10' },
              { bg: 'bg-rose-50', text: 'text-rose-600', hover: 'group-hover:bg-rose-600', border: 'hover:border-rose-200', shadow: 'hover:shadow-rose-900/10' },
              { bg: 'bg-amber-50', text: 'text-amber-600', hover: 'group-hover:bg-amber-600', border: 'hover:border-amber-200', shadow: 'hover:shadow-amber-900/10' },
              { bg: 'bg-emerald-50', text: 'text-emerald-600', hover: 'group-hover:bg-emerald-600', border: 'hover:border-emerald-200', shadow: 'hover:shadow-emerald-900/10' },
              { bg: 'bg-violet-50', text: 'text-violet-600', hover: 'group-hover:bg-violet-600', border: 'hover:border-violet-200', shadow: 'hover:shadow-violet-900/10' },
              { bg: 'bg-cyan-50', text: 'text-cyan-600', hover: 'group-hover:bg-cyan-600', border: 'hover:border-cyan-200', shadow: 'hover:shadow-cyan-900/10' },
            ];
            const color = colors[index % colors.length];

            return (
              <motion.div 
                key={notice.id} 
                initial={{ opacity: 0, scale: 0.9 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ delay: index * 0.05 }}
                onClick={() => handleNoticeClick(notice.id)}
                className={`group relative bg-white border border-gray-100 p-8 rounded-[2.5rem] cursor-pointer ${color.border} hover:shadow-2xl ${color.shadow} transition-all flex flex-col items-center text-center aspect-square justify-center`}
              >
                {notice.assigned_to && (
                  <div className="absolute top-0 right-0 m-4 px-3 py-1 bg-amber-100 text-amber-700 rounded-full text-[9px] font-black uppercase tracking-widest flex items-center gap-1 shadow-sm">
                    <Users size={10} /> Directed
                  </div>
                )}
                
                <div className={`w-24 h-24 rounded-[2rem] ${color.bg} ${color.text} flex items-center justify-center mb-6 transition-all ${color.hover} group-hover:text-white shadow-sm group-hover:rotate-6`}>
                  <FileText size={36} />
                </div>
                
                <div>
                  <h3 className={`text-2xl font-black text-gray-900 group-hover:${color.text} transition-colors line-clamp-2`}>{notice.title}</h3>
                </div>

                {isAdmin && (
                  <div className="absolute top-6 right-6 flex flex-col gap-2 opacity-0 group-hover:opacity-100 transition-all scale-75 group-hover:scale-100">
                    <button 
                      onClick={(e) => handleEdit(notice, e)} 
                      className="w-10 h-10 flex items-center justify-center bg-amber-50 text-amber-600 rounded-xl hover:bg-amber-100 transition-colors border border-amber-100 shadow-sm"
                    >
                      <Edit2 size={16} />
                    </button>
                    <button 
                      onClick={(e) => handleDelete(notice.id, e)} 
                      className="w-10 h-10 flex items-center justify-center bg-rose-50 text-rose-600 rounded-xl hover:bg-rose-100 transition-colors border border-rose-100 shadow-sm"
                    >
                      <Trash2 size={16} />
                    </button>
                  </div>
                )}
              </motion.div>
            );
          })
        )}
      </div>
    </div>
  );
};

export default RequirementsPanel;
